import Field from '@arcgis/core/layers/support/Field';
import CodedValueDomain from '@arcgis/core/layers/support/CodedValueDomain';
import request from '@arcgis/core/request';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import IdentifyResult from '@arcgis/core/rest/support/IdentifyResult';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import MapView from '@arcgis/core/views/MapView';
import * as esriIdentify from '@arcgis/core/rest/identify';
import Graphic from '@arcgis/core/Graphic';
import FindParameters from '@arcgis/core/rest/support/FindParameters';
import FindResult from '@arcgis/core/rest/support/FindResult';
import * as find from '@arcgis/core/rest/find';
import Geometry from '@arcgis/core/geometry/Geometry';
import { arcgisToGeoJSON, geojsonToArcGIS } from '@terraformer/arcgis';
import WebMap from '@arcgis/core/WebMap';
import Layer from '@arcgis/core/layers/Layer';
import Query from '@arcgis/core/rest/support/Query';
import * as query from '@arcgis/core/rest/query';
import FeatureSet from '@arcgis/core/rest/support/FeatureSet';
import Polygon from '@arcgis/core/geometry/Polygon';
import Color from '@arcgis/core/Color';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import { ISagtFeature } from '../_models/SagtFeature';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Point from '@arcgis/core/geometry/Point';
import Extent from '@arcgis/core/geometry/Extent';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import JsZip from 'jszip';
import { FileHelpers } from './FileHelpers';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import Home from '@arcgis/core/widgets/Home';
import Zoom from '@arcgis/core/widgets/Zoom';
import { ArxThematicOptions } from '@arxit/esri/widgets/ArxThematic';

export interface LayerDescription {
  fields: Field[];
}

export class EsriHelpers {
  static readonly MAX_NUMBER_OF_FEATURE_TO_QUERY = 1000;

  private static highlightPointSymbol: any = {
    type: 'simple-marker',
    style: 'square',
    color: '#00F6DE',
    size: 16,
  };
  private static highlightPolylineSymbol: any = {
    type: 'simple-line',
    color: '#00F6DE',
    width: 3,
    cap: 'round',
    join: 'round',
  };
  private static highlightPolygoneSymbol: any = {
    type: 'simple-fill',
    color: '#00F6DE',
    style: 'solid',
    outline: {
      color: 'white',
      width: 1,
    },
  };

  public static defaultPointSymbol: any = {
    // @ts-ignore
    type: 'simple-marker', // autocasts as SimpleMarkerSymbol
    style: 'square',
    color: 'red',
    size: '16px',
    outline: {
      // autocasts as SimpleLineSymbol
      color: [255, 255, 0],
      width: 3,
    },
  };
  public static defaultPolylineSymbol: any = {
    // @ts-ignore
    type: 'simple-line', // autocasts as SimpleLineSymbol
    color: [4, 90, 141],
    width: 3,
    cap: 'round',
    join: 'round',
  };
  public static defaultPolygonSymbol: any = {
    // @ts-ignore
    type: 'simple-fill', // autocasts as SimpleFillSymbol
    color: 'purple',
    style: 'solid',
    outline: {
      // autocasts as SimpleLineSymbol
      color: 'white',
      width: 1,
    },
  };

  public static setSymbols(symbols: any): void {
    if (symbols) {
      if (symbols.point) {
        EsriHelpers.defaultPointSymbol = symbols.point;
      }
      if (symbols.polyline) {
        EsriHelpers.defaultPolylineSymbol = symbols.polyline;
      }
      if (symbols.polygon) {
        EsriHelpers.defaultPolygonSymbol = symbols.polygon;
      }
    }
  }

  public static async GenerateMapAsync(
    container: string | HTMLDivElement | undefined,
    webMapId: string,
    layers: Layer[] = [],
    extraProperties: { [key: string]: any; activateIdentify: boolean }
  ): Promise<MapView> {
    try {
      const webMap = new WebMap({
        portalItem: {
          id: webMapId,
        },
      });
      const mapViewProperties = Object.assign({}, extraProperties, {
        container,
        map: webMap,
      });

      const homeContainer = document.createElement('div');
      homeContainer.style.position = 'absolute';
      homeContainer.style.bottom = '100px';
      homeContainer.style.right = '40px';
      const zoomContainer = document.createElement('div');
      zoomContainer.style.position = 'absolute';
      zoomContainer.style.bottom = '20px';
      zoomContainer.style.right = '40px';

      const mapView = new MapView(mapViewProperties);
      mapView.popup.autoOpenEnabled = extraProperties.activateIdentify;
      mapView.popup.highlightEnabled = true;
      mapView.ui.padding = { top: 10, left: 40, right: 40, bottom: 10 };
      mapView.ui.remove('zoom');
      mapView.ui.remove('attribution');

      EsriHelpers.enableHighlightOnPopup(mapView);

      mapView.ui.add([
        {
          component: new Home({
            view: mapView,
            container: homeContainer,
          }),
          position: 'manual',
        },
        {
          component: new Zoom({
            view: mapView,
            container: zoomContainer,
          }),
          position: 'manual',
        },
      ]);

      if (layers.length) {
        mapView.map.addMany(layers);
      }

      await webMap.when();
      await mapView.when();
      await reactiveUtils.whenOnce(() => mapView.updating);

      await EsriHelpers.WaitAllLayersLoaded(mapView);

      mapView.map.allLayers.forEach((l) => console.debug(l.id, l));

      return mapView;
    } catch (error) {
      console.error(error);
      return new MapView();
    }
  }

  public static WaitAllLayersLoaded(mapView: MapView): Promise<void> {
    return new Promise<void>((resolve) => {
      const numberOfLayers = mapView.map.allLayers.length;
      let numberOfLoadedLayers = 0;

      mapView.map.allLayers.forEach((layer) => {
        layer.on('layerview-create', () => {
          numberOfLoadedLayers++;
          console.debug(`Layer loaded: ${layer.title} (${numberOfLoadedLayers}/${numberOfLayers})`);
        });
        layer.on('layerview-create-error', () => {
          numberOfLoadedLayers++;
          console.debug(`Layer loaded (error): ${layer.title} (${numberOfLoadedLayers}/${numberOfLayers})`);
        });
      });

      const t = setInterval(() => {
        if (numberOfLoadedLayers >= numberOfLayers) {
          clearInterval(t);
          resolve();
        }
      }, 100);
    });
  }

  /**
   * Ecoute les changements de la propriété features de mapView.popup
   * Remets les features dans l'ordre des thématiques
   * @param mapView
   * @param thematics
   */
  public static listenToPopupFeaturesChange(mapView: MapView, thematics: ArxThematicOptions[]) {
    mapView.popup.watch('features', (features: (Graphic & { sourceLayer: __esri.Layer })[]) => {
      if (mapView.popup.get('ignoreFeatures') === 'true' || features.length < 2) {
        mapView.popup.set('ignoreFeatures', 'false');
        return;
      }

      console.debug('[popup] features', features);
      const orederedFeatures: (Graphic & { sourceLayer: __esri.Layer })[] = [];

      thematics.forEach((t) => {
        features.forEach((f) => {
          const layer = f.sourceLayer;
          if (layer instanceof FeatureLayer) {
            if (t.layersId?.includes(layer.id)) {
              orederedFeatures.push(f);
            }
          } else if (layer instanceof Sublayer) {
            if (t.layersId?.includes((layer as unknown as any).parent?.id)) {
              orederedFeatures.push(f);
            }
          }
        });
      });

      console.debug('[popup] ordered features', orederedFeatures);

      mapView.popup.set('ignoreFeatures', 'true');
      mapView.popup.features = orederedFeatures;
    });
  }

  /**
    In case of FeatureLayer, the selected feature is automatically highlighted
    In case of MapImageLayer, the selected feature is not highlighted, should be done by code
  */
  public static enableHighlightOnPopup(mapView: MapView) {
    mapView.popup.watch('selectedFeature', (graphic) => {
      console.debug('enableHighlightOnPopup.selectedFeature', graphic);
      if (graphic && graphic.sourceLayer && graphic.sourceLayer.layer && graphic.sourceLayer.layer.type !== 'feature') {
        mapView.graphics.removeAll();

        const outlineColor =
          mapView.highlightOptions && mapView.highlightOptions.color
            ? [
                mapView.highlightOptions.color.r,
                mapView.highlightOptions.color.g,
                mapView.highlightOptions.color.b,
                mapView.highlightOptions.color.a,
              ]
            : [0, 255, 255, 1];

        const clone = graphic.clone();

        console.debug('enableHighlightOnPopup.selectedFeature : MapImageLayer', clone, outlineColor);
        if (clone.geometry) {
          switch (clone.geometry.type) {
            case 'extent':
            case 'polygon':
              clone.set('symbol', {
                type: 'simple-fill',
                color: [0, 0, 0, 0],
                outline: {
                  color: outlineColor,
                  width: 1,
                },
              });
              break;
            case 'polyline':
              clone.set('symbol', {
                type: 'simple-line',
                color: outlineColor,
                width: 2,
              });
              break;
            case 'point':
            case 'multipoint':
              clone.set('symbol', {
                type: 'simple-marker',
                style: 'circle',
                color: [0, 0, 0, 0],
                size: 22,
                outline: {
                  color: outlineColor,
                  width: 3,
                },
              });
              break;
          }
          mapView.graphics.add(clone);
        }
      }
    });
    mapView.popup.watch('visible', (visible) => {
      if (!visible) {
        mapView.graphics.removeAll();
      }
    });
  }

  public static async getLayerDescription(url: string): Promise<LayerDescription | null> {
    try {
      const response = await request(url, {
        responseType: 'json',
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async getDomainCodedValues(
    urlOrDescription: string | LayerDescription,
    domainName: string
  ): Promise<{
    domain: CodedValueDomain | undefined;
    layerDesc: LayerDescription;
  } | null> {
    try {
      const desc = typeof urlOrDescription === 'string' ? await EsriHelpers.getLayerDescription(urlOrDescription) : urlOrDescription;

      return desc
        ? {
            layerDesc: desc,
            domain: desc.fields
              .filter((x) => x.domain && x.domain.type === 'coded-value' && x.domain.name.toLowerCase() === domainName.toLowerCase())
              .map((x) => x.domain as CodedValueDomain)
              .pop(),
          }
        : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  public static async query(url: string, queryParams: Query): Promise<FeatureSet> {
    try {
      if (!url) {
        return new FeatureSet();
      }

      return await query.executeQueryJSON(url, queryParams);
    } catch (error) {
      console.error(error);
      return new FeatureSet();
    }
  }

  public static async identify(
    layer: string | MapImageLayer | FeatureLayer | undefined,
    identifyParameters: IdentifyParameters
  ): Promise<IdentifyResult[]> {
    try {
      if (!layer) {
        return [];
      }

      const response: {
        results: IdentifyResult[];
        exceededTransferLimit: boolean;
      } = await esriIdentify.identify(typeof layer === 'string' ? layer : layer.url, identifyParameters);
      return response.results;
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  public static async find(layer: MapImageLayer | FeatureLayer | undefined, findParameters: FindParameters): Promise<FindResult[]> {
    try {
      if (!layer) {
        return [];
      }

      const response: {
        results: FindResult[];
        exceededTransferLimit: boolean;
      } = await find.find(layer.url, findParameters);
      return response.results;
    } catch (error) {
      throw error;
    }
  }

  public static togglePopupTemplate(mapView: MapView, graphics: Graphic[]): void {
    mapView.popup.clear();

    if (graphics.length === 0) {
      mapView.popup.close();
      return;
    }

    const features: Graphic[] = graphics.map((graphic) => {
      if (!graphic.popupTemplate) {
        const content = `<div class='popup-template-container'>
                              ${Object.keys(graphic.attributes)
                                .map(
                                  (k) =>
                                    `<span class='popup-template-container__key'>${k}</span>
                                    <span>${graphic.attributes[k]}</span>`
                                )
                                .join('')}
                              </div>`;
        // @ts-ignore
        graphic.popupTemplate = {
          title: `{popupTitle}`,
          content,
        };
      }
      return graphic;
    });
    console.debug(features);

    mapView.goTo(features).then(() => {
      mapView.popup.open({
        features,
      });
    });
  }

  public static loadGraphics(
    featureUrl: string,
    start: number,
    withGeometry?: boolean,
    spRef?: SpatialReference,
    orderByFields?: string[],
    filters?: Map<string, string>,
    useFieldNameForHeader?: boolean
  ) {
    return new Promise<Graphic[]>(async (resolve, reject) => {
      const arrFilter = filters && filters.size > 0 ? Array.from(filters.values()).filter((x) => x && x.length > 0) : [];

      const myQuery = new Query();
      if (withGeometry && spRef) {
        myQuery.returnGeometry = true;
        myQuery.outSpatialReference = spRef;
      } else {
        myQuery.returnGeometry = false;
      }
      myQuery.outFields = ['*'];
      myQuery.where = arrFilter.length > 0 ? arrFilter.join(' AND ') : '1=1';
      myQuery.start = start;
      myQuery.num = EsriHelpers.MAX_NUMBER_OF_FEATURE_TO_QUERY;

      if (orderByFields && orderByFields.length > 0) {
        myQuery.orderByFields = orderByFields;
      }

      try {
        const featureLayer = new FeatureLayer({ url: featureUrl });
        await featureLayer.load();

        const featureSet: FeatureSet = await query.executeQueryJSON(featureUrl, myQuery);

        const results = featureSet.features.map((x) => {
          const formattedAttributes = EsriHelpers.extractAttributes(featureLayer, x, useFieldNameForHeader);
          formattedAttributes._layer = featureUrl;
          x.attributes = formattedAttributes;
          return x;
        });

        resolve(results);
      } catch (error) {
        console.error(error);
        resolve([]);
      }
    });
  }

  public static extractAttributes(featureLayer: FeatureLayer | Sublayer, graphic: Graphic, useFieldNameForHeader?: boolean): any {
    const formattedAttributes: { [key: string]: any } = {};

    for (const field of featureLayer.fields) {
      const nameOfFieldToDisplayInHeader = useFieldNameForHeader ? field.name : field.alias;

      const originalValue = graphic.attributes[field.name];

      if (field.name === featureLayer.typeIdField) {
        for (const type of featureLayer.types) {
          if (type.id === graphic.attributes[field.name]) {
            formattedAttributes[nameOfFieldToDisplayInHeader] = `${type.name} (${type.id})`;
          }
        }
      } else if (field.type === 'date') {
        try {
          formattedAttributes[nameOfFieldToDisplayInHeader] = EsriHelpers.formatDate(new Date(originalValue), true, true);
        } catch {
          formattedAttributes[nameOfFieldToDisplayInHeader] = originalValue;
        }
      } else if (!field.domain) {
        formattedAttributes[nameOfFieldToDisplayInHeader] = originalValue;
      } else {
        let found = false;
        for (const type of featureLayer.types) {
          // check only if the sub type is from the parent type
          if (type.id === graphic.attributes[featureLayer.typeIdField] && type.domains[field.name] && type.domains[field.name].name) {
            formattedAttributes[nameOfFieldToDisplayInHeader] = (type.domains[field.name] as CodedValueDomain).getName(
              graphic.attributes[field.name]
            );
            found = true;
            break;
          }
        }
        if (found) {
          continue;
        }

        if (field.domain && field.domain.type === 'coded-value') {
          const domain = featureLayer.getFieldDomain(field.name, {
            feature: graphic,
          });
          formattedAttributes[nameOfFieldToDisplayInHeader] = (domain as CodedValueDomain).getName(graphic.attributes[field.name]);
        } else {
          formattedAttributes[nameOfFieldToDisplayInHeader] = null;
        }
      }
    }

    return formattedAttributes;
  }

  public static exportAllGraphicsToCsv(
    featureServerUrl: string,
    filters?: Map<string, string>,
    useFieldNameForHeader?: boolean
  ): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      const featureLayer = new FeatureLayer({
        url: featureServerUrl,
        outFields: ['*'],
      });

      await featureLayer.load();

      const max = await EsriHelpers.countGraphics(featureServerUrl, filters);

      const numberOfRounds = Math.ceil(max / EsriHelpers.MAX_NUMBER_OF_FEATURE_TO_QUERY);
      const promises: Array<Promise<Graphic[]>> = [];

      for (let i = 0; i < numberOfRounds; i++) {
        promises.push(
          EsriHelpers.loadGraphics(
            featureServerUrl,
            i * EsriHelpers.MAX_NUMBER_OF_FEATURE_TO_QUERY,
            false,
            undefined,
            undefined,
            filters,
            useFieldNameForHeader
          )
        );
      }

      const allResults = await Promise.all(promises);
      if (!allResults.length) {
        resolve('');
        return;
      }

      const graphics = allResults.reduce((prev, curr) => prev.concat(curr));

      resolve(EsriHelpers.formatGraphicsToCsv(featureLayer, featureLayer.fields, graphics));
    });
  }

  public static async countGraphics(featureUrl: string, filters?: Map<string, string>) {
    let count = -1;

    try {
      const myQuery = new Query();
      const arrFilter = filters && filters.size > 0 ? Array.from(filters.values()).filter((x) => x && x.length > 0) : [];
      myQuery.where = arrFilter.length > 0 ? arrFilter.join(' AND ') : '1=1';
      count = await query.executeForCount(featureUrl, myQuery);
    } catch (error) {
      console.error(error);
    }

    return count;
  }

  /**
   * Refresh all the layers if the view is ready
   */
  public static refreshView(view: MapView): void {
    if (view.ready) {
      try {
        view.map.allLayers.forEach((layer) => {
          // @ts-ignore
          if (layer.refresh) {
            // @ts-ignore
            layer.refresh();
          }
        });
      } catch {}
    }
  }

  public static createGraphicFromDawEvents(
    view: MapView,
    drawAction: 'point' | 'multipoint' | 'polyline' | 'polygon' | 'rectangle' | 'circle' | 'ellipse',
    result: any,
    color?: any
  ): Graphic {
    view.graphics.removeAll();

    let geometry: any;
    let symbol: any;

    switch (drawAction) {
      case 'point':
        geometry = {
          type: 'point',
          x: result.coordinates[0],
          y: result.coordinates[1],
          spatialReference: view.spatialReference,
        };
        symbol = EsriHelpers.defaultPointSymbol;
        break;
      case 'polyline':
        geometry = {
          type: 'polyline',
          paths: result.vertices,
          spatialReference: view.spatialReference,
        };
        symbol = EsriHelpers.defaultPolylineSymbol;
        break;
      case 'polygon':
        geometry = {
          type: 'polygon',
          rings: result.vertices,
          spatialReference: view.spatialReference,
        };
        symbol = EsriHelpers.defaultPolygonSymbol;
        if (color) {
          symbol.color = color;
        }
        break;
    }

    return new Graphic({
      geometry,
      symbol,
    });
  }

  public static GraphicToSagtFeature(graphic: Graphic): ISagtFeature {
    try {
      const sagtFeature: ISagtFeature = {
        geometry: EsriHelpers.geometryToGeoJSON(graphic.geometry),
        color: graphic.geometry.type === 'polygon' && graphic.symbol ? graphic.symbol.color?.toCss(true) : undefined,
        idComposante: graphic.geometry.type === 'polyline' && graphic.attributes ? graphic.attributes.idComposante : undefined,
        id: graphic.attributes ? graphic.attributes.id : undefined,
      };

      return sagtFeature;
    } catch (error) {
      console.error(error);
      return {
        id: undefined,
        color: undefined,
        geometry: undefined,
        idComposante: undefined,
      };
    }
  }

  public static SagtFeatureToGraphic(feature: ISagtFeature): Graphic {
    try {
      let graphic: Graphic;
      const geometry = geojsonToArcGIS(feature.geometry) as any;
      geometry.spatialReference = new SpatialReference({ wkid: 2056 });

      if (geometry.hasOwnProperty('paths')) {
        geometry.type = 'polyline';
        graphic = new Graphic({
          geometry,
          symbol: this.defaultPolylineSymbol,
          attributes: {
            idComposante: feature.idComposante,
            id: feature.id,
          },
        });
      } else if (geometry.hasOwnProperty('rings')) {
        geometry.type = 'polygon';
        graphic = new Graphic({
          geometry,
          symbol: this.defaultPolygonSymbol,
          attributes: {
            color: feature.color,
            id: feature.id,
          },
        });
        if (feature.color) {
          graphic.symbol.color = Color.fromString(EsriHelpers.hexStringToRgbaString(feature.color));
        }
      } else if (geometry.hasOwnProperty('points')) {
        geometry.type = 'multipoint';
        graphic = new Graphic({
          geometry,
          symbol: this.defaultPointSymbol,
          attributes: {
            id: feature.id,
          },
        });
      } else {
        geometry.type = 'point';
        graphic = new Graphic({
          geometry,
          symbol: this.defaultPointSymbol,
          attributes: {
            id: feature.id,
          },
        });
      }

      return graphic;
    } catch (error) {
      console.error(error);
      return new Graphic();
    }
  }

  public static geometryToGeoJSON(geometry: Geometry): undefined | any {
    try {
      const geom: any = arcgisToGeoJSON(geometry);
      console.debug(geom);
      return geom;
    } catch {
      return undefined;
    }
  }

  /**
   * Highlights the graphics
   * Use default symbol or the one selected
   */
  public static highlight(view: MapView, graphics: Graphic[], symbol?: any): void {
    view.graphics.removeAll();
    const newGraphics = graphics.map((x) => {
      if (symbol) {
        return new Graphic({
          geometry: x.geometry,
          symbol,
        });
      } else {
        return new Graphic({
          geometry: x.geometry,
          symbol: this.getSymbol(x.geometry.type),
        });
      }
    });

    view.graphics.addMany(newGraphics);
  }

  public static getPointOnGeometry(geometry: Geometry): Point {
    switch (geometry.type) {
      case 'point':
        return geometry as Point;
      case 'extent':
        return new Point({
          x: (geometry as Extent).xmin,
          y: (geometry as Extent).ymin,
          spatialReference: (geometry as Extent).spatialReference,
        });
      case 'polygon':
      case 'polyline':
        return (geometry as Polygon).getPoint(0, 0);
      case 'multipoint':
        return (geometry as Multipoint).getPoint(0);
      default:
        return new Point({ x: 0, y: 0 });
    }
  }

  private static getSymbol(geometryType: string): any {
    switch (geometryType) {
      case 'point':
        return this.highlightPointSymbol;
      case 'polyline':
        return this.highlightPolylineSymbol;
      case 'polygon':
        return this.highlightPolygoneSymbol;
    }
  }

  public static rgbaStringToHexString(rgba?: string): string {
    if (!rgba) {
      return '#00000066';
    }
    if (rgba.startsWith('#')) {
      return rgba;
    }
    const parts = rgba.substring(rgba.indexOf('(')).split(',');
    const r = parseInt(this.trim(parts[0].substring(1)), 10);
    const g = parseInt(this.trim(parts[1]), 10);
    const b = parseInt(this.trim(parts[2]), 10);

    const hexA = this.trim(parts[3].substring(0, parts[3].length - 1));
    const a = this.decimalStringToHexString(hexA);

    return `#${r.toString(16)}${g.toString(16)}${b.toString(16)}${a}`;
  }

  public static hexStringToRgbaString(hex?: string): string {
    if (!hex) {
      return 'rgba(0,0,0,0.5)';
    }

    const shortHex = hex.substring(1, hex.length);
    const parts: string[] = new Array(4);
    parts[0] = shortHex.substring(0, 2);
    parts[1] = shortHex.substring(2, 4);
    parts[2] = shortHex.substring(4, 6);
    parts[3] = shortHex.substring(6, 8);
    const r = parseInt(this.trim(parts[0]), 16);
    const g = parseInt(this.trim(parts[1]), 16);
    const b = parseInt(this.trim(parts[2]), 16);
    const a = this.hexStringToDecimal(this.trim(parts[3]));

    return `rgba(${r},${g},${b},${a})`;
  }

  public static rgbaToArray(rgba: string): number[] {
    const parts = rgba.substring(rgba.indexOf('(')).split(',');
    const r = parseInt(this.trim(parts[0].substring(1)), 10);
    const g = parseInt(this.trim(parts[1]), 10);
    const b = parseInt(this.trim(parts[2]), 10);
    const a = Number(parseFloat(this.trim(parts[3].substring(0, parts[3].length - 1))).toFixed(2));

    return [r, g, b, a];
  }

  /** Format should be YYYY-MM-DD HH:MM:SS */
  public static formatDateToStringForQuery(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    const ss = date.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hh}:${mm}:${ss}`;
  }

  public static downloadExport(
    csvs: {
      content: string;
      filenameWithExtension: string;
    }[],
    zipName: string
  ) {
    const universalBOM = '\uFEFF';
    const zip = JsZip();

    const promises = csvs.map((csv) => {
      const url = 'data:text/csv; charset=utf-8,' + encodeURIComponent(universalBOM + csv.content);
      return fetch(url).then((resp) => resp.blob());
    });

    Promise.all(promises).then((blobs) => {
      blobs.forEach((blob, index) => {
        //@ts-ignore
        zip.file(csvs[index].filenameWithExtension, blob);
      });
      zip.generateAsync({ type: 'blob' }).then((zipFile) => {
        return FileHelpers.saveAs(zipFile, zipName);
      });
    });
  }

  public static formatDate(date: Date, withTime?: boolean, withSecond?: boolean) {
    const options: Intl.DateTimeFormatOptions = withTime
      ? {
          hour: '2-digit',
          minute: '2-digit',
        }
      : {};
    if (withSecond) {
      options.second = '2-digit';
    }
    return date.toLocaleDateString('fr-CH', options);
  }

  private static trim(str: string): string {
    return str.replace(/^\s+|\s+$/gm, '');
  }

  private static decimalStringToHexString(a: string): string {
    return Math.trunc(parseFloat(a) * 255)
      .toString(16)
      .toUpperCase()
      .padStart(2, '0');
  }

  private static hexStringToDecimal(a: string): number {
    let nb = Number(parseInt(a, 16) / 255);

    if (isNaN(nb)) {
      nb = 0.5;
    }

    return nb;
  }

  private static formatGraphicsToCsv(featureLayer: FeatureLayer, fields: Field[], graphics: Graphic[]): string {
    const data: Array<string> = [];

    if (graphics.length > 0) {
      data.push(fields.map((x) => x.alias).join(';'));
      for (const graphic of graphics) {
        const row: string[] = [];

        for (const field of fields) {
          const fieldRawValue = graphic.attributes[field.name];
          row.push(fieldRawValue);
        }

        data.push(row.join(';'));
      }

      return data.join('\r\n');
    }

    return '';
  }
}
